import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Backdrop from "@mui/material/Backdrop";
import { IoChevronBackCircleSharp } from "react-icons/io5";
import { FaRegThumbsUp, FaThumbsUp } from "react-icons/fa";
import Header from "./Header";
import AlertMessage from "../../common/components/AlertMessage";
import * as APIService from "../../common/api/index.js";
import URL from "../../common/api/ConstantURL";
import { getSessionContext } from "../../common/utils/sessionContext";
import { formatNoteDateTime, isNoteAcknowledged, useNotesTruncation } from "../../common/utils/noteUtils";
import * as Sentry from "@sentry/react";
import "../../styles/Notes.css";

const AllNotes = () => {
  const navigate = useNavigate();
  const { caregiverId, communityId } = getSessionContext();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alertStatus, setAlertStatus] = useState("");
  const [expandedNotes, setExpandedNotes] = useState({});
  const [truncatedNotes, setTruncatedNotes] = useState({});
  const messageNodesRef = useRef({});

  const toggleExpanded = (noteId) => {
    setExpandedNotes((prev) => ({
      ...prev,
      [noteId]: !prev[noteId],
    }));
  };

  const toggleLikeNote = (noteId) => {
    const targetNote = notes.find((note) => String(note.noteId) === String(noteId));
    if (!targetNote) return;
    if (isNoteAcknowledged(targetNote)) return;

    const acknowledgeNote = async () => {
      setLoading(true);
      try {
        const query = new URLSearchParams({
          noteId: targetNote.noteId,
          caregiverId: caregiverId || "",
        }).toString();
        const responseData = await APIService.putData(`${URL.acknowledgeNote}?${query}`);
        const acknowledgeSuccess = responseData?.status === "success" || responseData?.code === 200;
        if (!acknowledgeSuccess) {
          setAlertStatus({
            message: responseData?.message || "Failed to acknowledge note",
            status: "400",
            origin: "center",
          });
          return;
        }
        setAlertStatus({
          message: responseData?.message || "Note acknowledged successfully",
          status: "200",
          origin: "center",
        });
        setNotes((prevNotes) =>
          prevNotes.map((note) =>
            String(note.noteId) === String(noteId)
              ? {
                  ...note,
                  acknowledgedStatus: true,
                  acknowledgeFlag: 1,
                }
              : note,
          ),
        );
      } catch (error) {
        Sentry.captureException(error);
        setAlertStatus({
          message: "Error while acknowledging note",
          status: "400",
          origin: "center",
        });
      } finally {
        setLoading(false);
      }
    };

    acknowledgeNote();
  };

  const setMessageNode = (noteId) => (node) => {
    if (node) {
      messageNodesRef.current[noteId] = node;
    } else {
      delete messageNodesRef.current[noteId];
    }
  };

  useEffect(() => {
    const loadNotes = async () => {
      if (!caregiverId) return;
      setLoading(true);
      try {
        const query = new URLSearchParams({
          caregiverId: caregiverId || "",
          communityId: communityId || "",
        }).toString();
        const responseData = await APIService.getData(`${URL.getAllNotesForCaregiver}?${query}`);
        if (responseData?.status === "success" || responseData?.code === 200) {
          const fetchedNotes = Array.isArray(responseData?.data) ? responseData.data : [];
          setNotes(fetchedNotes);
        } else {
          setAlertStatus({
            message: responseData?.message || "Failed to fetch notes",
            status: "400",
            origin: "center",
          });
        }
      } catch (error) {
        Sentry.captureException(error);
        setAlertStatus({
          message: "Error while fetching all client notes",
          status: "400",
          origin: "center",
        });
      } finally {
        setLoading(false);
      }
    };

    loadNotes();
  }, [caregiverId, communityId]);

  useNotesTruncation({
    notes,
    expandedNotes,
    messageNodesRef,
    setTruncatedNotes,
    getNoteId: (note) => note.noteId,
  });

  return (
    <>
      <Header />
      <div className="home-container">
        <Backdrop sx={{ color: "#ffba30", zIndex: (theme) => theme.zIndex.drawer + 1000 }} open={loading}>
          <img src={require("../../assets/btTree.png")} className="loading-img" alt="Loading" />
        </Backdrop>
        {alertStatus ? <AlertMessage status={alertStatus.status} message={alertStatus.message} origin={alertStatus.origin} closeAlert={() => setAlertStatus("")} /> : null}

        <div className="home-inner-container notes-list-page">
          <div className="heading vitals-heading notes-page-heading">
            <IoChevronBackCircleSharp className="vitals-preview-header" onClick={() => navigate(-1)} />
            <span className="notes-page-title">All Clients - Notes</span>
            <span></span>
          </div>

          <div className="notes-section all-notes-cards-wrapper">
            {notes.length > 0 ? (
              <>
                <div className="notes-section-header">
                  <h3 className="notes-section-title">Recent Notes</h3>
                </div>
                <div className="notes-section-body">
                  <div className="caregiver-notes-single-card">
                    <div className="caregiver-notes-single-card-scroll">
                      {notes.map((item, index) => (
                        <div key={item.noteId} className="caregiver-note-item" onClick={() => toggleExpanded(item.noteId)}>
                          <div className="all-notes-header-row">
                            <div className="all-notes-left">
                              <span className="all-notes-top-text">From: {item.caregiverName || "Unknown"}</span>
                              <span className="all-notes-top-text">Client: {item.clientName || ""}</span>
                            </div>
                            <div className="all-notes-right">
                              <span className="caregiver-note-time">{formatNoteDateTime(item)}</span>
                            </div>
                          </div>
                          <p ref={setMessageNode(String(item.noteId))} className={`caregiver-note-message ${notes.length === 1 || expandedNotes[item.noteId] ? "expanded" : "collapsed"}`}>
                            {item.description || "No description available"}
                          </p>
                          <div className="all-notes-actions-row">
                            {notes.length > 1 && (expandedNotes[item.noteId] || truncatedNotes[String(item.noteId)]) ? (
                              <button
                                type="button"
                                className="notes-read-more-btn"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  toggleExpanded(item.noteId);
                                }}>
                                {expandedNotes[item.noteId] ? "Read less" : "Read more"}
                              </button>
                            ) : (
                              <span></span>
                            )}
                            <div className="all-notes-inline-actions">
                              <button
                                type="button"
                                className={`all-notes-icon-btn ${isNoteAcknowledged(item) ? "liked" : ""}`}
                                disabled={isNoteAcknowledged(item)}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  toggleLikeNote(item.noteId);
                                }}
                                aria-label="Acknowledge note">
                                {isNoteAcknowledged(item) ? <FaThumbsUp /> : <FaRegThumbsUp />}
                              </button>
                            </div>
                          </div>
                          {index < notes.length - 1 ? <div className="caregiver-note-item-divider" /> : null}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="notes-section-empty notes-section-empty-full">No notes available</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AllNotes;
